import { useRef } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { LinksStepValues } from '../validators';
import { colors } from '../../../core/theme/theme';

interface StepLinksProps {
  links: LinksStepValues;
  onChange: (field: keyof LinksStepValues, value: string) => void;
  photo: File | null;
  onPhotoChange: (file: File | null) => void;
  errors: Partial<Record<keyof LinksStepValues, string>>;
}

export default function StepLinks({ links, onChange, photo, onPhotoChange, errors }: StepLinksProps) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {t('projects.wizard.stepLinks', 'Links & Media')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('projects.wizard.linksAllOptional', 'All fields below are optional — leave blank to skip.')}
      </Typography>

      <TextField
        label={t('projects.wizard.sharepointLink', 'SharePoint link')}
        fullWidth
        value={links.sharepointLink}
        onChange={(e) => onChange('sharepointLink', e.target.value)}
        error={Boolean(errors.sharepointLink)}
        helperText={errors.sharepointLink}
        sx={{ mb: 3 }}
      />

      <TextField
        label={t('projects.wizard.teamsLink', 'Teams link')}
        fullWidth
        value={links.teamsLink}
        onChange={(e) => onChange('teamsLink', e.target.value)}
        error={Boolean(errors.teamsLink)}
        helperText={errors.teamsLink}
        sx={{ mb: 3 }}
      />

      {/* Photo upload */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('projects.wizard.photo', 'Project photo')}
      </Typography>

      {photo ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            src={URL.createObjectURL(photo)}
            sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 0 }}
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>{photo.name}</Typography>
            <Button size="small" onClick={() => fileRef.current?.click()}>
              {t('projects.wizard.photoChange', 'Change')}
            </Button>
            <Button size="small" color="secondary" onClick={() => onPhotoChange(null)}>
              {t('projects.wizard.photoRemove', 'Remove')}
            </Button>
          </Box>
        </Box>
      ) : (
        <Button
          variant="contained"
          onClick={() => fileRef.current?.click()}
          sx={{ bgcolor: colors.yellow, color: '#000', '&:hover': { bgcolor: colors.yellowHover } }}
        >
          {t('projects.wizard.photoSelect', 'Select a photo')}
        </Button>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t('projects.wizard.photoHelp', 'JPG/PNG, up to 10 MB. Optional.')}
      </Typography>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          if (file && file.size <= 10 * 1024 * 1024) onPhotoChange(file);
          if (fileRef.current) fileRef.current.value = '';
        }}
      />
    </Box>
  );
}
